import { TooltipWebV3 } from '@components/TaskList/TooltipWebV3';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Text } from '@components/Restyle';
import {
  BottomToast,
  BottomToastHandle,
} from '@components/shared/BottomToast.web';
import Icon from '@components/shared/Icon/Icon';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import {
  useCreateInviteMutation,
  useCompleteWebTutorialFlowsMutation,
  WebTutorialFlow,
  GetMeDocument,
} from '@graphql/generated';
import useMe from '@hooks/useMe';
import {
  CLIPBOARD_FAILURE_MESSAGE_KEYS,
  ClipboardFailureReason,
  copyTextToClipboard,
} from '@utils/copyTextToClipboard';
import {
  shouldShowPaywallForProject,
  ProjectPaywallShape,
} from '@utils/teams/paywall';

type TaskShareLinkProps = {
  taskId: string;
  project?: ProjectPaywallShape | null;
  canEdit: boolean;
  hasTitle?: boolean;
  accessibilityLabel?: string;
  noMarginRight?: boolean;
  isTaskPanel?: boolean;
  /** Hover tint (TA-9005). Defaults to grey01; callers whose surface is
   * already grey01 on hover (e.g. a hovered task row) pass grey02. */
  hoverBackgroundColor?: 'grey01' | 'grey02';
};

type TooltipMode = 'hover' | 'copied' | null;

export const TaskShareLink: React.FC<TaskShareLinkProps> = ({
  taskId,
  project,
  canEdit,
  hasTitle = false,
  accessibilityLabel = 'Share task',
  noMarginRight = false,
  isTaskPanel = false,
  hoverBackgroundColor = 'grey01',
}) => {
  const { me } = useMe();
  const { t } = useTranslation();
  // Same red-icon failure toast as MediaViewer's copy-link (TA-10845).
  const copyFailedToastRef = useRef<BottomToastHandle>(null);

  const showCopyFailedToast = useCallback(
    (reason: ClipboardFailureReason) => {
      copyFailedToastRef.current?.show(
        t(CLIPBOARD_FAILURE_MESSAGE_KEYS[reason]),
        { duration: 3000, icon: 'warning' }
      );
    },
    [t]
  );
  const { setPaywallModalOpen, setPaywallTeam } = useWebDrawer();

  // Tooltip state machine:
  // - 'hover'  : show "Copy Link to Invite"
  // - 'copied' : show "Link copied!"
  // - null     : hidden
  const [tooltipMode, setTooltipMode] = useState<TooltipMode>(null);

  // When we just showed "copied", we don't want hover tooltip to re-appear
  // until the mouse actually leaves and re-enters.
  const [suppressHoverTooltip, setSuppressHoverTooltip] = useState(false);

  // Ghost icon hover tint (TA-9005): tracked separately from tooltipMode so
  // the grey01 tint still shows while the hover tooltip is suppressed.
  const [isIconHovered, setIconHovered] = useState(false);

  // Keep a timer ref to avoid setState after unmount / repeated clicks.
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) {
        clearTimeout(copiedTimerRef.current);
        copiedTimerRef.current = null;
      }
    };
  }, []);

  const [completeWebTutorialFlows] = useCompleteWebTutorialFlowsMutation({
    refetchQueries: [{ query: GetMeDocument }, 'getMe'],
  });

  // Copy link to clipboard and show "Link copied!" tooltip.
  const copyToClipboard = useCallback(
    async (link: string) => {
      if (!link) return;
      const result = await copyTextToClipboard(link);
      if (!result.success) {
        showCopyFailedToast(result.reason);
        return;
      }

      // Force "copied" mode and block hover mode until mouse leaves.
      setSuppressHoverTooltip(true);
      setTooltipMode('copied');

      // Reset timer if user clicks multiple times quickly.
      if (copiedTimerRef.current) {
        clearTimeout(copiedTimerRef.current);
      }

      copiedTimerRef.current = setTimeout(() => {
        // IMPORTANT:
        // Close tooltip directly instead of switching back to hover title,
        // to avoid flashing during Popper fade-out transition.
        setTooltipMode(null);
        // Do NOT setSuppressHoverTooltip(false) here.
        // We unlock it only on actual mouse leave.
      }, 3000);
    },
    [showCopyFailedToast]
  );

  const [createInviteMutation, { data: createInviteData }] =
    useCreateInviteMutation({
      onError: () => showCopyFailedToast('link-generation-failed'),
      // createInvite is nullable — a resolved-but-empty link is a
      // generation failure too, not a silent no-op (TA-10845).
      onCompleted: (data) => {
        if (!data?.createInvite) showCopyFailedToast('link-generation-failed');
      },
    });

  useEffect(() => {
    if (!createInviteData?.createInvite) return;
    const formattedLink = createInviteData.createInvite.replace(
      'register/register-with-email',
      'invited-user'
    );
    copyToClipboard(formattedLink);
  }, [createInviteData, copyToClipboard]);

  // Trigger invite creation and link copy.
  const handleCopy = useCallback(async () => {
    if (!canEdit) return;

    // Check paywall using project.team.subscriptionDetails and project.currentUserTeamRole (no extra API)
    if (project && shouldShowPaywallForProject(project)) {
      setPaywallTeam(project?.team ?? undefined);
      setPaywallModalOpen(true);
      return;
    }

    if (!me?.completedWebTutorialFlows?.includes('invite_member')) {
      completeWebTutorialFlows({
        variables: { flowType: WebTutorialFlow.InviteMember },
      });
    }

    createInviteMutation({ variables: { taskId } });
  }, [
    canEdit,
    me,
    completeWebTutorialFlows,
    createInviteMutation,
    taskId,
    project,
    setPaywallTeam,
    setPaywallModalOpen,
  ]);

  // When user cannot edit, show a disabled icon without tooltip.
  if (!canEdit) {
    return (
      <Icon
        variant={isTaskPanel ? 'l' : 's'}
        name='Link'
        color='grey03'
        marginRight={noMarginRight ? undefined : 's'}
      />
    );
  }
  const isOpen = tooltipMode !== null;

  const showPaywall = project && shouldShowPaywallForProject(project);

  return (
    <Box position='relative'>
      <TooltipWebV3
        marginOffset={8}
        open={isOpen}
        isTaskPanel={isTaskPanel}
        disableHoverListener
        disableFocusListener
        disableTouchListener
        placement='bottom-end'
        variant={tooltipMode === 'hover' ? 'dark' : 'light'}
        title={
          tooltipMode === 'copied' ? (
            <Box flexDirection='row'>
              <Icon name='CheckCircle' color='greenSecondary' variant='s' />
              <Text ml='xs' variant='labelSmall' color='black'>
                Link copied!
              </Text>
            </Box>
          ) : (
            <Text ml='xxs' variant='labelSmall' color='white'>
              {showPaywall
                ? 'Upgrade to unlock team features'
                : 'Copy Link to Invite'}
            </Text>
          )
        }>
        {/* Use a single stable DOM anchor to avoid layout shifts */}
        <div
          aria-label={accessibilityLabel}
          data-ignore-row-click
          onMouseEnter={() => {
            setIconHovered(true);
            // Only allow hover tooltip when not suppressed.
            if (!suppressHoverTooltip) {
              setTooltipMode('hover');
            }
          }}
          onMouseLeave={() => {
            setIconHovered(false);
            // Always close tooltip on leave.
            setTooltipMode(null);

            // Unlock hover tooltip only after user actually leaves.
            setSuppressHoverTooltip(false);
          }}
          style={{ display: 'inline-flex', cursor: 'pointer' }}
          onClick={handleCopy}>
          {hasTitle ? (
            <Box
              width={122}
              flexDirection='row'
              justifyContent='center'
              borderRadius='xs'
              paddingVertical='xxs'
              backgroundColor={
                isIconHovered ? hoverBackgroundColor : undefined
              }>
              <Icon color='blue' variant='s' name='Link' marginRight='xs' />
              <Text flex={1} variant='labelSmall' color='blue'>
                Copy Link
              </Text>
            </Box>
          ) : (
            <Box
              padding='xxs'
              borderRadius='xs'
              marginRight={noMarginRight ? undefined : 's'}
              backgroundColor={
                isIconHovered ? hoverBackgroundColor : undefined
              }>
              <Icon
                variant={isTaskPanel ? 'l' : 's'}
                name='LinkNew'
                color='grey05'
              />
            </Box>
          )}
        </div>
      </TooltipWebV3>
      <BottomToast ref={copyFailedToastRef} />
    </Box>
  );
};

TaskShareLink.displayName = 'TaskShareLink';
